import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Video, Phone, PhoneOff, Mic, MicOff, VideoOff, Circle, Download,
  FileText, Trash2, AlertTriangle, Loader2, Lock, Captions, CaptionsOff,
  Sparkles, Image as ImageIcon, Upload, X, ShieldCheck, BookOpen,
} from 'lucide-react';
import { connect as twilioConnect, createLocalTracks, LocalDataTrack } from 'twilio-video';
import {
  GaussianBlurBackgroundProcessor,
  VirtualBackgroundProcessor,
  isSupported as isProcessorSupported,
} from '@twilio/video-processors';
import { toast } from 'sonner';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import useActivityFeed from '../hooks/useActivityFeed';

// Twilio video-processors WASM/JS workers are copied to /public/twilio-video-processors.
var PROCESSOR_ASSETS_PATH = '/twilio-video-processors';

// Built-in preset backgrounds (rendered as <img> objects passed to VirtualBackgroundProcessor).
var PRESET_BACKGROUNDS = [
  { id: 'pirateship', label: 'Pirate', src: '/video-bg/pirateship.jpg' },
  { id: 'castle', label: 'Castle', src: '/video-bg/castle.jpg' },
  { id: 'nature', label: 'Nature', src: '/video-bg/nature.jpg' },
  { id: 'office', label: 'Office', src: '/video-bg/office.jpg' },
];

/**
 * Video Calls — Standard+ feature, gated server-side via /api/video/capabilities.
 * - 45-minute hard cap (server enforces, client warns at 5/1 minutes remaining).
 * - Recording defaults ON; participant can opt out mid-call.
 * - 60-day retention on Standard, indefinite on Pro (delete required by user).
 */

function fmtDuration(sec) {
  if (!sec && sec !== 0) return '—';
  var m = Math.floor(sec / 60);
  var s = sec % 60;
  return m + 'm ' + (s < 10 ? '0' : '') + s + 's';
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
}

function StatusPill({ status }) {
  var color = '#6B6690', label = status;
  if (status === 'pending') { color = '#F59E0B'; label = 'Pending'; }
  if (status === 'in-progress') { color = '#10B981'; label = 'Live'; }
  if (status === 'processing') { color = '#5A4E9E'; label = 'Processing'; }
  if (status === 'completed') { color = '#2D2560'; label = 'Completed'; }
  if (status === 'declined') { color = '#9CA3AF'; label = 'Declined'; }
  if (status === 'failed') { color = '#EF4444'; label = 'Failed'; }
  if (status === 'purged') { color = '#9CA3AF'; label = 'Purged'; }
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: color + '20', color: color, border: '1px solid ' + color + '40' }}>
      {label}
    </span>
  );
}

function Participant({ participant, isLocal, displayName }) {
  var videoRef = useRef(null);
  var audioRef = useRef(null);

  useEffect(function () {
    var attachTrack = function (track) {
      if (track.kind === 'video' && videoRef.current) track.attach(videoRef.current);
      if (track.kind === 'audio' && audioRef.current) track.attach(audioRef.current);
    };
    var detachTrack = function (track) {
      if (track && track.detach) track.detach().forEach(function (el) { el.remove(); });
    };
    participant.tracks.forEach(function (pub) { if (pub.track) attachTrack(pub.track); });
    var onSubscribed = function (track) { attachTrack(track); };
    var onUnsubscribed = function (track) { detachTrack(track); };
    participant.on('trackSubscribed', onSubscribed);
    participant.on('trackUnsubscribed', onUnsubscribed);
    return function () {
      participant.removeListener('trackSubscribed', onSubscribed);
      participant.removeListener('trackUnsubscribed', onUnsubscribed);
      participant.tracks.forEach(function (pub) { if (pub.track) detachTrack(pub.track); });
    };
  }, [participant]);

  return (
    <div className="relative rounded-xl overflow-hidden bg-black aspect-video border border-[#DDE1EE]">
      <video ref={videoRef} autoPlay playsInline muted={isLocal} className="w-full h-full object-cover" />
      <audio ref={audioRef} autoPlay />
      <div
        className="absolute bottom-2 left-2 px-3 py-1 rounded-full bg-white text-[#2D2560] text-xs font-semibold shadow-sm"
        data-testid={isLocal ? 'video-name-pill-local' : 'video-name-pill-remote'}
      >
        {displayName || (isLocal ? 'You' : (participant.identity || 'Co-parent'))}
      </div>
    </div>
  );
}

function BackgroundSelector(props) {
  var bgChoice = props.bgChoice;
  var applying = props.applying;
  var savedBgs = props.savedBgs || []; // [{ id, blobUrl, filename, ... }]
  var savedLimitReached = props.savedLimitReached;
  var maxSaved = props.maxSaved || 3;
  var onPick = props.onPick;
  var onPickCustomFile = props.onPickCustomFile;
  var onDeleteSaved = props.onDeleteSaved;
  var fileInputRef = props.fileInputRef;
  var disabled = props.disabled;
  var compact = props.compact;

  var options = [
    { id: 'none', label: 'Off', icon: <X className="w-4 h-4" /> },
    { id: 'blur', label: 'Blur', icon: <Sparkles className="w-4 h-4" /> },
  ].concat(PRESET_BACKGROUNDS.map(function (p) { return { id: p.id, label: p.label, src: p.src }; }));

  function btnClass(active) {
    return 'group relative flex flex-col items-center justify-center gap-1 rounded-lg border text-xs font-medium transition-all '
      + (compact ? 'h-12 w-16 ' : 'h-16 w-20 ')
      + (active
        ? 'border-[#5A4E9E] bg-[#5A4E9E] text-white shadow-sm'
        : 'border-[#DDE1EE] bg-white text-[#3A3566] hover:border-[#5A4E9E] hover:bg-[#F6F7FC]');
  }

  return (
    <div className={'mt-4 ' + (compact ? 'card-surface p-3' : 'border-t border-[#DDE1EE] pt-4')} data-testid="video-bg-selector">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#5A4E9E] flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Virtual background
        </p>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#6B6690]" data-testid="video-bg-saved-count">
            Saved: {savedBgs.length}/{maxSaved}
          </span>
          {applying && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#5A4E9E]" />}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {options.map(function (opt) {
          var active = bgChoice === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={function () { onPick(opt.id); }}
              disabled={disabled || applying}
              className={btnClass(active) + ' overflow-hidden disabled:opacity-40'}
              data-testid={'video-bg-' + opt.id}
              aria-pressed={active}>
              {opt.src ? (
                <img src={opt.src} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
              ) : null}
              <span className={'relative z-10 flex flex-col items-center gap-0.5 ' + (opt.src ? 'text-white drop-shadow' : '')}>
                {opt.icon || <ImageIcon className="w-4 h-4" />}
                <span className="text-[10px]">{opt.label}</span>
              </span>
            </button>
          );
        })}

        {/* User's saved backgrounds */}
        {savedBgs.map(function (bg) {
          var bgKey = 'saved:' + bg.id;
          var active = bgChoice === bgKey;
          return (
            <div key={bg.id} className="relative group" data-testid={'video-bg-saved-' + bg.id}>
              <button
                type="button"
                onClick={function () { onPick(bgKey); }}
                disabled={disabled || applying}
                className={btnClass(active) + ' overflow-hidden disabled:opacity-40'}
                aria-pressed={active}>
                {bg.blobUrl ? (
                  <img src={bg.blobUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
                ) : (
                  <Loader2 className="w-4 h-4 animate-spin text-[#5A4E9E]" />
                )}
                <span className="relative z-10 flex flex-col items-center gap-0.5 text-white drop-shadow">
                  <span className="text-[10px]">Saved</span>
                </span>
              </button>
              <button
                type="button"
                onClick={function (e) { e.stopPropagation(); onDeleteSaved(bg.id); }}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#EF4444] text-white shadow flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                aria-label="Delete saved background"
                data-testid={'video-bg-delete-' + bg.id}>
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}

        {/* Custom upload — only if quota remaining */}
        {!savedLimitReached && (
          <button
            type="button"
            onClick={function () { if (fileInputRef.current) fileInputRef.current.click(); }}
            disabled={disabled || applying}
            className={btnClass(false) + ' overflow-hidden disabled:opacity-40'}
            data-testid="video-bg-custom"
            aria-label="Upload a new background">
            <span className="relative z-10 flex flex-col items-center gap-0.5">
              <Upload className="w-4 h-4" />
              <span className="text-[10px]">Upload</span>
            </span>
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={function (e) {
            var f = e.target.files && e.target.files[0];
            if (f) onPickCustomFile(f);
            e.target.value = '';
          }}
          data-testid="video-bg-file-input"
        />
      </div>
      {savedLimitReached && (
        <p className="text-[11px] text-[#6B6690] mt-2" data-testid="video-bg-limit-msg">
          You've reached the {maxSaved} saved-background limit. Delete one to upload another.
        </p>
      )}
      {!isProcessorSupported && (
        <p className="text-[11px] text-[#EF4444] mt-2">Virtual backgrounds aren't supported in this browser. Try Chrome or Edge.</p>
      )}
    </div>
  );
}

export default function VideoCalls() {
  var { user: authUser } = useAuth();
  var selfName = (authUser && (authUser.name || authUser.email)) || 'You';
  var [caps, setCaps] = useState(null);
  var [rooms, setRooms] = useState([]);
  var [loading, setLoading] = useState(true);
  var [starting, setStarting] = useState(false);
  // Recording is permanently disabled at the product level — transcripts come
  // from live Web Speech captions, no audio/video is captured. The toggle has
  // been removed; the field on the API payload is hard-coded to `false`.
  var [activeRoom, setActiveRoom] = useState(null); // { room_sid, unique_name, token }
  var [twRoom, setTwRoom] = useState(null);
  var [participants, setParticipants] = useState([]);
  var [micEnabled, setMicEnabled] = useState(true);
  var [camEnabled, setCamEnabled] = useState(true);
  var [callEvents, setCallEvents] = useState([]); // unified activity feed (mic/cam/joins/etc.)
  var callEventIdRef = useRef(0);

  // ---------- Consent flow (re-enable recording / transcription) ----------
  // Local DataTrack used to send/receive peer-to-peer signaling messages
  // (captions broadcast + caption-request flow) over the existing Twilio
  // Video call. Persisted in a ref so async handlers can use it.
  var dataTrackRef = useRef(null);
  // The OTHER peer's RemoteDataTrack (if subscribed). Persisted for safety.
  var remoteDataTracksRef = useRef([]);
  // Outgoing caption request: { request_id, sent_at } — set when WE asked
  // the co-parent to enable their captions. Cleared on response or timeout.
  var [outgoingCaptionRequest, setOutgoingCaptionRequest] = useState(null);
  // Incoming caption request: { request_id, requester_name } — set when the
  // co-parent asked US to enable our captions. Rendered as a non-blocking
  // banner inside the call (no freeze, no blocking modal).
  var [incomingCaptionRequest, setIncomingCaptionRequest] = useState(null);
  // Pre-call wish from the caller asking us to also transcribe ourselves.
  // Cleared once the local user accepts/dismisses the banner.
  var [dualPromptOpen, setDualPromptOpen] = useState(false);
  var captionRequestTimeoutRef = useRef(null);
  var [secondsLeft, setSecondsLeft] = useState(45 * 60);

  // Pre-call lobby ("preroom"): users can test their camera and mic before joining.
  // phase = 'lobby' → 'preroom' → 'in-call'. preroomMode tells us which backend call
  // to make on Join: 'new' creates a Twilio room; 'join' fetches a token for an
  // already-pending room initiated by the co-parent.
  var [phase, setPhase] = useState('lobby');
  var [preroomMode, setPreroomMode] = useState(null);
  var [preroomRoomToJoin, setPreroomRoomToJoin] = useState(null);
  var [previewTracks, setPreviewTracks] = useState(null);
  var [previewError, setPreviewError] = useState(null);
  var [previewMicEnabled, setPreviewMicEnabled] = useState(true);
  var [previewCamEnabled, setPreviewCamEnabled] = useState(true);
  // Pre-room caller setting: also ask the co-parent to transcribe themselves
  // when they accept the call. Sent as `request_dual_transcription` on POST /rooms.
  var [requestDualTranscription, setRequestDualTranscription] = useState(false);
  var previewVideoRef = useRef(null);

  // Virtual background state. bgChoice can be 'none' | 'blur' | 'office' | 'home' | 'nature' | ('saved:' + id)
  var [bgChoice, setBgChoice] = useState('none');
  var [savedBgs, setSavedBgs] = useState([]); // [{ id, blobUrl, content_type, ... }]
  var [bgApplying, setBgApplying] = useState(false);
  var bgProcessorRef = useRef(null); // Twilio processor currently attached to the local video track
  var customFileInputRef = useRef(null);

  // Real-time captions via the browser's Web Speech API. We buffer every final result
  // with a timestamp relative to call start, then flush to the backend on end-call.
  var [captions, setCaptions] = useState([]);          // committed (final) captions
  var [interimCaption, setInterimCaption] = useState(''); // current LOCAL in-flight phrase
  // Mirror of the REMOTE speaker's in-flight phrase, received over the
  // DataTrack. Local Web Speech API cannot transcribe the remote audio
  // (browser only hears the local mic), so each side broadcasts its own
  // captions to the peer for display.
  var [remoteInterimCaption, setRemoteInterimCaption] = useState('');
  var [remoteInterimSpeaker, setRemoteInterimSpeaker] = useState('');
  var [captionsOn, setCaptionsOn] = useState(true);
  var [speechSupported, setSpeechSupported] = useState(true);
  var recognitionRef = useRef(null);
  var callStartRef = useRef(null);
  // Tracks the last interim caption we broadcast over the DataTrack so we
  // don't re-send the same in-flight phrase on every tick.
  var lastBroadcastInterimRef = useRef('');

  // Activity-feed sticky-bottom scroll behaviour lives in a dedicated hook so
  // the dependency-order contract is impossible to break from this file.
  // See /app/frontend/src/hooks/useActivityFeed.js.
  var activityFeed = useActivityFeed({
    totalCount: callEvents.length + captions.length,
    watchKey: interimCaption + '|' + remoteInterimCaption,
  });
  var activityFeedRef = activityFeed.feedRef;
  var handleActivityScroll = activityFeed.onScroll;
  var jumpToLatest = activityFeed.jumpToLatest;
  var showJumpToLatest = activityFeed.showJumpToLatest;
  var unreadCount = activityFeed.unreadCount;


  // SpeechRecognition is finicky — it dies silently on long silences, network
  // blips, throws `InvalidStateError` on quick start/stop cycles, and gives
  // varying error types per browser. We wrap it with restart-on-end, error
  // classification, exponential backoff, and a watchdog that nukes & rebuilds
  // the recognizer if it stops emitting results while audio should be flowing.
  var recRestartAttemptsRef = useRef(0);
  var recLastResultAtRef = useRef(0);
  var recWatchdogRef = useRef(null);

  function _stopWatchdog() {
    if (recWatchdogRef.current) { clearInterval(recWatchdogRef.current); recWatchdogRef.current = null; }
  }

  function _startWatchdog() {
    _stopWatchdog();
    recWatchdogRef.current = setInterval(function () {
      // Only watchdog while in an active call with a remote present and captions
      // requested. Otherwise let it idle.
      if (!callStartRef.current) return;
      var rec = recognitionRef.current;
      if (!rec) return;
      var idleMs = Date.now() - (recLastResultAtRef.current || 0);
      // If we've been silent >25s, assume the recognizer died and rebuild it.
      // Real silences during a call should generate at least one onresult or
      // onend within this window in healthy Chrome/Edge.
      if (idleMs > 25000) {
        // eslint-disable-next-line no-console
        console.warn('[SpeechRecognition watchdog] idle ' + idleMs + 'ms, rebuilding');
        try { rec.onend = null; rec.abort(); } catch (e) { /* ignore */ }
        recognitionRef.current = null;
        startSpeechRecognition();
      }
    }, 5000);
  }

  function startSpeechRecognition() {
    var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechSupported(false);
      return;
    }
    // Prevent duplicate start.
    if (recognitionRef.current) return;
    var rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    recLastResultAtRef.current = Date.now();

    rec.onstart = function () {
      // Successful start — reset the backoff counter.
      recRestartAttemptsRef.current = 0;
      recLastResultAtRef.current = Date.now();
    };

    rec.onresult = function (event) {
      recLastResultAtRef.current = Date.now();
      var finalised = [];
      var interim = '';
      for (var i = event.resultIndex; i < event.results.length; i++) {
        var res = event.results[i];
        var text = res[0].transcript.trim();
        if (!text) continue;
        if (res.isFinal) {
          var elapsed = Math.floor((Date.now() - (callStartRef.current || Date.now())) / 1000);
          finalised.push({ speaker: selfName, text: text, start_seconds: elapsed, end_seconds: elapsed, final: true });
        } else {
          interim += ' ' + text;
        }
      }
      if (finalised.length > 0) setCaptions(function (prev) { return prev.concat(finalised); });
      setInterimCaption(interim.trim());

      // Bilateral transcription: broadcast both finalised + interim captions
      // to the peer over our LocalDataTrack so they see our side too. The
      // remote browser cannot hear our mic via Web Speech API; this is the
      // canonical fix for the "I only see my own captions" bug.
      try {
        if (finalised.length > 0) {
          for (var f = 0; f < finalised.length; f++) {
            sendDataTrackMessage({
              kind: 'caption',
              speaker: finalised[f].speaker,
              text: finalised[f].text,
              start_seconds: finalised[f].start_seconds,
              final: true,
            });
          }
        }
        // Interim: only send when it actually changed AND is non-empty, to
        // avoid spamming the data track with empty strings each tick.
        var nextInterim = interim.trim();
        if (nextInterim && nextInterim !== lastBroadcastInterimRef.current) {
          lastBroadcastInterimRef.current = nextInterim;
          sendDataTrackMessage({
            kind: 'caption',
            speaker: selfName,
            text: nextInterim,
            final: false,
          });
        } else if (!nextInterim && lastBroadcastInterimRef.current) {
          // We just finished an utterance — tell the peer to clear their
          // remote-interim line.
          lastBroadcastInterimRef.current = '';
          sendDataTrackMessage({ kind: 'caption', speaker: selfName, text: '', final: false });
        }
      } catch (broadcastErr) { /* DataTrack may not be ready — silent ignore */ }
    };

    rec.onerror = function (e) {
      // eslint-disable-next-line no-console
      console.warn('[SpeechRecognition] error:', e && e.error);
      var err = (e && e.error) || '';
      // Permanent failures — give up and surface in the UI.
      if (err === 'not-allowed' || err === 'service-not-allowed') {
        setSpeechSupported(false);
        try { rec.onend = null; } catch (ignored) { /* ignore */ }
        recognitionRef.current = null;
        return;
      }
      // Transient failures (no-speech, aborted, audio-capture, network): let
      // onend fire and the restart path handle the comeback.
    };

    rec.onend = function () {
      // Auto-restart unless the call has ended OR we've explicitly stopped.
      if (recognitionRef.current !== rec) return;
      if (!callStartRef.current) {
        recognitionRef.current = null;
        return;
      }
      // Exponential backoff to avoid hot-loops if Chrome refuses to restart.
      recRestartAttemptsRef.current = (recRestartAttemptsRef.current || 0) + 1;
      var delay = Math.min(8000, 200 * Math.pow(2, Math.min(recRestartAttemptsRef.current, 6)));
      var attempt = recRestartAttemptsRef.current;
      // eslint-disable-next-line no-console
      console.info('[SpeechRecognition] restart in ' + delay + 'ms (attempt ' + attempt + ')');
      setTimeout(function () {
        // Stop if conditions changed (call ended, captions toggled off, etc.).
        if (recognitionRef.current !== rec) return;
        if (!callStartRef.current) { recognitionRef.current = null; return; }
        try { rec.start(); }
        catch (err) {
          // InvalidStateError or already-started — recognition is in a bad
          // state. Build a fresh one.
          try { rec.abort(); } catch (e) { /* ignore */ }
          recognitionRef.current = null;
          startSpeechRecognition();
        }
      }, delay);
    };

    try { rec.start(); }
    catch (err) {
      // If start throws synchronously (rare — usually an already-running rec),
      // schedule a clean rebuild.
      // eslint-disable-next-line no-console
      console.warn('[SpeechRecognition] start threw, scheduling rebuild', err);
      setTimeout(function () { recognitionRef.current = null; startSpeechRecognition(); }, 500);
      return;
    }
    recognitionRef.current = rec;
    _startWatchdog();
  }

  function stopSpeechRecognition() {
    _stopWatchdog();
    var rec = recognitionRef.current;
    recognitionRef.current = null;
    recRestartAttemptsRef.current = 0;
    if (rec) {
      try { rec.onend = null; rec.onerror = null; rec.onresult = null; rec.abort(); } catch (err) { /* ignored */ }
    }
    setInterimCaption('');
  }

  function flushCaptionsToServer(roomSid, segments) {
    if (!roomSid || segments.length === 0) return Promise.resolve();
    return axios.post(API + '/video/rooms/' + roomSid + '/live-captions',
      { segments: segments }, { withCredentials: true })
      .catch(function (e) { console.error('caption flush failed', e); });
  }

  // Defensive safety net: ensure live transcription is running whenever the
  // user has captions ON and at least one remote peer is present. Catches edge
  // cases where the explicit start-points (joinFromPreroom + participantConnected)
  // raced or a previous SpeechRecognition died silently between calls.
  useEffect(function () {
    if (phase !== 'in-call') return;
    if (!captionsOn || !speechSupported) return;
    if (participants.length === 0) return;
    if (recognitionRef.current) return;
    startSpeechRecognition();
  }, [phase, captionsOn, speechSupported, participants.length]);

  var loadCaps = useCallback(function () {
    return axios.get(API + '/video/capabilities', { withCredentials: true })
      .then(function (r) { setCaps(r.data); })
      .catch(function (e) { setCaps({ can_call: false, tier: 'free' }); console.error(e); });
  }, []);

  var loadRooms = useCallback(function () {
    return axios.get(API + '/video/rooms', { withCredentials: true })
      .then(function (r) { setRooms((r.data && r.data.items) || []); })
      .catch(function (e) { console.error(e); });
  }, []);

  useEffect(function () {
    Promise.all([loadCaps(), loadRooms()]).finally(function () { setLoading(false); });
    var poll = setInterval(loadRooms, 30000);
    return function () { clearInterval(poll); };
  }, [loadCaps, loadRooms]);

  // Active call: countdown timer + auto-end at 45 min
  useEffect(function () {
    if (!twRoom) return undefined;
    var startMs = Date.now();
    var maxSec = (activeRoom && activeRoom.max_duration_seconds) || (4 * 60 * 60);
    var id = setInterval(function () {
      var elapsed = Math.floor((Date.now() - startMs) / 1000);
      var left = Math.max(0, maxSec - elapsed);
      setSecondsLeft(left);
      if (left === 5 * 60) toast.warning('5 minutes remaining on this call.');
      if (left === 60) toast.warning('1 minute remaining on this call.');
      if (left <= 0) endCall();
    }, 1000);
    return function () { clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twRoom]);

  function enterPreroom(mode, room) {
    setPhase('preroom');
    setPreroomMode(mode);
    setPreroomRoomToJoin(room || null);
    setPreviewError(null);
    setCallEvents([]);
    callEventIdRef.current = 0;
    callStartRef.current = Date.now();
    // Local-only entry into the waiting room (no server log yet for 'new' mode
    // since the room hasn't been created — we still surface it in the live
    // activity feed so the user sees a clear chronology).
    callEventIdRef.current += 1;
    setCallEvents([{
      id: callEventIdRef.current,
      kind: 'event',
      type: 'waiting_room_entered',
      at_seconds: 0,
      who: selfName,
      ts: Date.now(),
    }]);
    if (mode === 'join' && room && room.room_sid) {
      logCallEvent(room.room_sid, 'waiting_room_entered', { speaker_name: selfName });
    }
    // Auto-mute mic and camera when answering an incoming call so the receiver
    // never goes live before they're ready. Initiators (mode === 'new') keep
    // their devices on by default since they chose to start the call.
    var startMuted = mode === 'join';
    setPreviewMicEnabled(!startMuted);
    setPreviewCamEnabled(!startMuted);
    createLocalTracks({
      audio: true,
      video: {
        // Higher capture resolution = noticeably sharper virtual background.
        // Browsers downscale transparently if the webcam doesn't support 720p.
        width: { ideal: 1280, min: 640 },
        height: { ideal: 720, min: 360 },
        frameRate: { ideal: 30, min: 15 },
      },
    })
      .then(function (tracks) {
        if (startMuted) {
          tracks.forEach(function (t) {
            try { t.disable(); } catch (e) { /* ignore */ }
          });
        }
        setPreviewTracks(tracks);
        // Attach video preview when DOM is ready.
        var vid = tracks.find(function (t) { return t.kind === 'video'; });
        if (vid && previewVideoRef.current) vid.attach(previewVideoRef.current);
      })
      .catch(function (err) {
        setPreviewError((err && err.message) || 'Could not access camera/mic.');
      });
  }

  // Re-attach video track once the <video> element mounts.
  useEffect(function () {
    if (phase !== 'preroom' || !previewTracks || !previewVideoRef.current) return;
    var vid = previewTracks.find(function (t) { return t.kind === 'video'; });
    if (vid) vid.attach(previewVideoRef.current);
  }, [phase, previewTracks]);

  function togglePreviewMic() {
    if (!previewTracks) return;
    previewTracks.forEach(function (t) {
      if (t.kind === 'audio') { if (previewMicEnabled) t.disable(); else t.enable(); }
    });
    setPreviewMicEnabled(!previewMicEnabled);
  }

  function togglePreviewCam() {
    if (!previewTracks) return;
    previewTracks.forEach(function (t) {
      if (t.kind === 'video') { if (previewCamEnabled) t.disable(); else t.enable(); }
    });
    setPreviewCamEnabled(!previewCamEnabled);
  }

  function cancelPreroom() {
    if (previewTracks) {
      previewTracks.forEach(function (t) { try { t.stop(); } catch (e) { /* ignore */ } });
    }
    // Detach any active processor before discarding tracks.
    detachBackgroundProcessor();
    setPreviewTracks(null);
    setPreroomMode(null);
    setPreroomRoomToJoin(null);
    setRequestDualTranscription(false);
    setPhase('lobby');
  }

  // ---------------- Virtual background helpers ----------------
  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        // Force decode before handing to the processor — prevents black frames
        // when the canvas pipeline tries to read an undecoded image on iOS/Safari.
        if (img.decode) {
          img.decode().then(function () { resolve(img); }).catch(function () { resolve(img); });
        } else {
          resolve(img);
        }
      };
      img.onerror = function (e) { reject(e); };
      img.src = src;
    });
  }

  function getLocalVideoTrack() {
    // During preroom, the LocalVideoTrack lives in previewTracks. During an active
    // call, it lives on twRoom.localParticipant.videoTracks.
    if (previewTracks) {
      var found = previewTracks.find(function (t) { return t.kind === 'video'; });
      if (found) return found;
    }
    if (twRoom) {
      var pubs = Array.from(twRoom.localParticipant.videoTracks.values());
      if (pubs.length > 0 && pubs[0].track) return pubs[0].track;
    }
    return null;
  }

  function detachBackgroundProcessor() {
    var track = getLocalVideoTrack();
    var proc = bgProcessorRef.current;
    bgProcessorRef.current = null;
    if (track && proc) {
      try { track.removeProcessor(proc); } catch (e) { /* ignore */ }
    }
  }

  async function applyBackground(choice) {
    if (!isProcessorSupported) {
      toast.error('Virtual backgrounds are not supported in this browser. Try Chrome or Edge.');
      return;
    }
    var track = getLocalVideoTrack();
    if (!track) {
      toast.error('Camera not ready yet.');
      return;
    }
    setBgApplying(true);
    try {
      // Always detach the previous processor first.
      detachBackgroundProcessor();

      if (choice === 'none') {
        setBgChoice('none');
        return;
      }

      var processor;
      if (choice === 'blur') {
        processor = new GaussianBlurBackgroundProcessor({
          assetsPath: PROCESSOR_ASSETS_PATH,
          // Larger mask blur = softer, more natural edges around the person.
          maskBlurRadius: 10,
          // Heavier background blur = more cinematic / bokeh look.
          blurFilterRadius: 25,
          // Chrome perf win: compute mask in parallel with frame downscale.
          deferInputFrameDownscale: true,
          // Temporal smoothing to kill the "flicker halo" around moving edges.
          hysteresis: true,
        });
      } else {
        var src;
        if (choice && choice.indexOf('saved:') === 0) {
          var savedId = choice.slice(6);
          var saved = savedBgs.find(function (b) { return b.id === savedId; });
          if (!saved || !saved.blobUrl) {
            toast.error('Saved background not ready.');
            return;
          }
          src = saved.blobUrl;
        } else {
          var preset = PRESET_BACKGROUNDS.find(function (p) { return p.id === choice; });
          if (!preset) return;
          src = preset.src;
        }
        var img = await loadImage(src);
        processor = new VirtualBackgroundProcessor({
          assetsPath: PROCESSOR_ASSETS_PATH,
          backgroundImage: img,
          maskBlurRadius: 10,
          fitType: 'Cover',
          deferInputFrameDownscale: true,
          hysteresis: true,
        });
      }

      await processor.loadModel();
      // Twilio @twilio/video-processors v3.x BackgroundProcessor.processFrame()
      // writes to the output canvas via getContext('2d') || getContext('bitmaprenderer').
      // Setting outputFrameBufferContextType: 'webgl2' here makes both lookups return
      // null and the result is a BLACK canvas. The official docs recommend the pair below.
      try {
        track.addProcessor(processor, {
          inputFrameBufferType: 'video',
          outputFrameBufferContextType: 'bitmaprenderer',
        });
      } catch (e) {
        // Older twilio-video versions don't accept these options — fall back to default.
        // eslint-disable-next-line no-console
        console.warn('addProcessor primary options failed, retrying with defaults:', e);
        track.addProcessor(processor);
      }
      bgProcessorRef.current = processor;
      setBgChoice(choice);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Virtual background failed:', e);
      toast.error('Could not apply background. Try again.');
      setBgChoice('none');
    } finally {
      setBgApplying(false);
    }
  }

  // Fetch the user's saved backgrounds, downloading each image as a blob URL
  // (so the canvas-based VirtualBackgroundProcessor can use it without CORS issues).
  var loadSavedBgs = useCallback(function () {
    return axios.get(API + '/video/backgrounds', { withCredentials: true })
      .then(function (r) {
        var items = (r.data && r.data.items) || [];
        return Promise.all(items.map(function (it) {
          return axios.get(API + '/video/backgrounds/' + it.id + '/file', {
            withCredentials: true,
            responseType: 'blob',
          })
            .then(function (resp) {
              return Object.assign({}, it, { blobUrl: URL.createObjectURL(resp.data) });
            })
            .catch(function () { return Object.assign({}, it, { blobUrl: null }); });
        }));
      })
      .then(function (withBlobs) {
        // Revoke any previous blob URLs we no longer reference.
        setSavedBgs(function (prev) {
          var keepIds = new Set(withBlobs.map(function (b) { return b.id; }));
          prev.forEach(function (p) {
            if (!keepIds.has(p.id) && p.blobUrl) {
              try { URL.revokeObjectURL(p.blobUrl); } catch (e) { /* ignore */ }
            }
          });
          return withBlobs;
        });
      })
      .catch(function (e) { console.error('loadSavedBgs failed', e); });
  }, []);

  function onCustomBgSelected(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image too large (max 8MB).');
      return;
    }
    var fd = new FormData();
    fd.append('file', file);
    setBgApplying(true);
    axios.post(API + '/video/backgrounds', fd, { withCredentials: true })
      .then(function (resp) {
        var newId = resp.data && resp.data.id;
        // Refresh list, then auto-apply the freshly-uploaded one.
        return loadSavedBgs().then(function () {
          if (newId) applyBackground('saved:' + newId);
        });
      })
      .catch(function (e) {
        var msg = (e && e.response && e.response.data && e.response.data.detail) || 'Upload failed.';
        toast.error(msg);
      })
      .finally(function () { setBgApplying(false); });
  }

  function onDeleteSavedBg(id) {
    if (!window.confirm('Delete this saved background?')) return;
    axios.delete(API + '/video/backgrounds/' + id, { withCredentials: true })
      .then(function () {
        // If currently applied, fall back to 'none'.
        if (bgChoice === 'saved:' + id) {
          applyBackground('none');
        }
        toast.success('Background deleted');
        loadSavedBgs();
      })
      .catch(function () { toast.error('Could not delete background.'); });
  }

  // Load saved bgs once when the component mounts (user is already authenticated by then).
  useEffect(function () {
    loadSavedBgs();
  }, [loadSavedBgs]);

  // Clean up blob URLs on unmount.
  useEffect(function () {
    return function () {
      savedBgs.forEach(function (b) {
        if (b.blobUrl) { try { URL.revokeObjectURL(b.blobUrl); } catch (e) { /* ignore */ } }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function joinFromPreroom() {
    setStarting(true);
    var tracksToUse = previewTracks; // already initialised; we'll hand them to twilio-video
    var initialMicOn = previewMicEnabled;
    var initialCamOn = previewCamEnabled;
    // P2P signaling channel for the consent flow (re-enable rec/transcription).
    var dataTrack = new LocalDataTrack({ name: 'parentum-consent' });
    dataTrackRef.current = dataTrack;

    var meta$;
    if (preroomMode === 'new') {
      // Recording is disabled at the product level — transcription comes
      // from live Web Speech captions (no audio/video media captured). The
      // backend also forces recording_enabled=false defensively.
      meta$ = axios.post(API + '/video/rooms', {
        record: false,
        request_dual_transcription: !!requestDualTranscription,
      }, { withCredentials: true })
        .then(function (r) { return r.data; });
    } else if (preroomMode === 'join' && preroomRoomToJoin) {
      meta$ = axios.post(API + '/video/rooms/' + preroomRoomToJoin.room_sid + '/token', {}, { withCredentials: true })
        .then(function (r) { return r.data; });
    } else {
      setStarting(false);
      cancelPreroom();
      return;
    }

    meta$
      .then(function (meta) {
        setActiveRoom(meta);
        setPhase('in-call');
        return twilioConnect(meta.token, {
          name: meta.unique_name,
          tracks: tracksToUse || undefined,
          audio: tracksToUse ? undefined : true,
          video: tracksToUse ? undefined : { width: 640 },
        }).then(function (room) {
          // Publish the data track AFTER connect (Twilio doesn't allow mixing
          // `tracks: [...]` with `audio/video: ...` options at connect-time).
          try { room.localParticipant.publishTrack(dataTrack); } catch (e) { console.warn('publish dataTrack failed', e); }
          setTwRoom(room);
          setMicEnabled(initialMicOn);
          setCamEnabled(initialCamOn);
          callStartRef.current = Date.now();
          setCaptions([]);
          setInterimCaption('');
          // Live transcription kicks in the moment BOTH parties are present
          // (per legal flow — single-party transcription has no consent value).
          // If a remote already exists at connect-time, start now; otherwise
          // the participantConnected handler below will fire it.
          var remoteAlreadyHere = room.participants && room.participants.size > 0;
          if (captionsOn && remoteAlreadyHere) startSpeechRecognition();
          // Log the call_started event with the initial state of recording + captions.
          logCallEvent(meta.room_sid, 'call_started', {
            recording: !!meta.recording_enabled,
            captions: !!captionsOn,
            speaker_name: selfName,
          });
          // For the initiator (mode==='new') this is the moment the call is "placed".
          if (preroomMode === 'new') {
            pushLocalEvent('call_initiated', selfName, { roomSid: meta.room_sid });
          }
          // Callee opened a call that the caller flagged for dual transcription —
          // auto-enable their own captions so BOTH sides' voices are transcribed
          // from the start, and surface an informational banner so they know.
          if (preroomMode === 'join' && meta.dual_transcription_requested) {
            if (speechSupported) {
              setCaptionsOn(true);
              // The participantConnected/in-call effect will kick off recognition
              // as soon as the remote is present.
              if (remoteAlreadyHere) startSpeechRecognition();
              pushLocalEvent('dual_transcription_auto_enabled', selfName, { roomSid: meta.room_sid });
              setDualPromptOpen(true);
            } else {
              // Browser can't run Web Speech — auto-enable is impossible. Log it
              // so the caller can see in their feed that the request wasn't honored.
              pushLocalEvent('caption_request_declined', selfName, {
                roomSid: meta.room_sid,
                meta: { reason: 'speech_unsupported' },
              });
              toast.error('Co-parent requested dual transcription but live captions are not supported in this browser.');
            }
          }
          // Local user entering the live call.
          pushLocalEvent('participant_joined', selfName, { roomSid: meta.room_sid });
          // Capture initial mic/cam state in the activity feed.
          if (!initialMicOn) pushLocalEvent('mic_muted', selfName, { roomSid: meta.room_sid });
          if (!initialCamOn) pushLocalEvent('camera_off', selfName, { roomSid: meta.room_sid });
          var initial = [];
          room.participants.forEach(function (p) {
            initial.push(p);
            // Remote already in the room when we joined → log them.
            pushLocalEvent('participant_joined', resolveParticipantName(p.identity), { roomSid: meta.room_sid });
            attachRemoteTrackEvents(p, meta.room_sid);
          });
          setParticipants(initial);
          room.on('participantConnected', function (p) {
            setParticipants(function (prev) { return prev.concat([p]); });
            pushLocalEvent('participant_joined', resolveParticipantName(p.identity), { roomSid: meta.room_sid });
            attachRemoteTrackEvents(p, meta.room_sid);
            // ⭐ Live transcription auto-starts the instant the other party joins.
            // Re-check captionsOn each time in case the user toggled while waiting.
            if (captionsOn && speechSupported && !recognitionRef.current) {
              startSpeechRecognition();
            }
          });
          room.on('participantDisconnected', function (p) {
            setParticipants(function (prev) {
              var next = prev.filter(function (q) { return q !== p; });
              // ⭐ When the LAST remote leaves, stop live transcription. It will
              // auto-resume if/when another peer reconnects (rare but possible).
              if (next.length === 0 && recognitionRef.current) stopSpeechRecognition();
              return next;
            });
            pushLocalEvent('participant_left', resolveParticipantName(p.identity), { roomSid: meta.room_sid });
            // If a caption-request flow was in flight when they bailed, auto-clear
            // so we don't leave a stale banner behind.
            if (outgoingCaptionRequest || incomingCaptionRequest) {
              clearCaptionRequestState();
            }
          });
          room.on('disconnected', function () { cleanupAfterCall(); });
          // Preview tracks are now owned by the room — clear local handle.
          setPreviewTracks(null);
        });
      })
      .catch(function (err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail) || (err && err.message) || 'Could not connect to call.';
        toast.error(msg);
        cancelPreroom();
      })
      .finally(function () { setStarting(false); loadCaps(); loadRooms(); });
  }

  function startCall() {
    if (!caps || !caps.can_call) return;
    enterPreroom('new', null);
  }

  // Send an event to the room's timeline. `at_seconds` is computed from
  // callStartRef so a server-side ordering is preserved across both clients.
  function logCallEvent(roomSid, type, meta) {
    if (!roomSid) return Promise.resolve();
    var atSeconds = callStartRef.current
      ? Math.max(0, Math.floor((Date.now() - callStartRef.current) / 1000))
      : 0;
    return axios.post(API + '/video/rooms/' + roomSid + '/events',
      { type: type, at_seconds: atSeconds, meta: meta || {} },
      { withCredentials: true })
      .catch(function (e) { console.warn('event log failed', type, e); });
  }

  // Resolve a Twilio participant's display name from the active room metadata.
  // Falls back to the raw identity (a Mongo _id) if names aren't present.
  // Resolve a Twilio participant identity (== our Mongo user_id) to a
  // human-readable name. NEVER returns the raw user_id — that has been
  // observed leaking into transcript PDFs as "69ea806dec09daa0879ea2af joined
  // the call" when activeRoom hadn't yet been populated. Safe fallback is
  // always "Co-parent".
  function resolveParticipantName(identity) {
    if (!identity) return 'Co-parent';
    if (activeRoom) {
      if (identity === activeRoom.initiator_user_id) return activeRoom.initiator_name || 'Caller';
      if (identity === activeRoom.invited_user_id) return activeRoom.invited_user_name || activeRoom.partner_name || 'Co-parent';
    }
    // If the identity looks like a Mongo ObjectId (24 hex chars) OR a UUID,
    // we have no name to surface — show a generic label rather than the id.
    var s = String(identity);
    if (/^[a-f0-9]{24}$/i.test(s) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) {
      return 'Co-parent';
    }
    return s;
  }

  // Push an event into the live activity feed AND persist it on the backend so
  // it shows up in the post-call PDF. `who` is the human-readable speaker name.
  function pushLocalEvent(type, who, opts) {
    var atSeconds = callStartRef.current
      ? Math.max(0, Math.floor((Date.now() - callStartRef.current) / 1000))
      : 0;
    callEventIdRef.current += 1;
    var entry = {
      id: callEventIdRef.current,
      kind: 'event',
      type: type,
      at_seconds: atSeconds,
      who: who || '',
      ts: Date.now(),
    };
    setCallEvents(function (prev) { return prev.concat([entry]).slice(-50); });
    var rid = (opts && opts.roomSid) || (activeRoom && activeRoom.room_sid) || (preroomRoomToJoin && preroomRoomToJoin.room_sid);
    if (rid) {
      logCallEvent(rid, type, Object.assign({ speaker_name: who || '' }, (opts && opts.meta) || {}));
    }
  }

  // ============================================================
  // Caption-request flow (non-blocking ask co-parent to transcribe)
  // ============================================================
  //
  // Captioning your OWN voice never needs the other party's permission — it's
  // just the local Web Speech API listening to YOUR mic. Captioning the OTHER
  // party's voice, however, requires THEIR browser to run its own recognizer
  // and broadcast the text via Twilio DataTracks (the Web Speech API can only
  // hear the local microphone). So "request co-parent transcription" is really
  // a polite ask sent over the DataTrack — the other side gets a non-blocking
  // banner and can Accept (turns on their captions) or Decline (call continues
  // unchanged). Either way the call is never paused or frozen.

  // Generate a short request id (good enough for matching req → response).
  function _makeReqId() {
    return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
  }

  // Send a JSON message over our LocalDataTrack. No-op if not connected.
  function sendDataTrackMessage(payload) {
    var t = dataTrackRef.current;
    if (!t) return;
    try { t.send(JSON.stringify(payload)); } catch (e) { console.warn('dataTrack send failed', e); }
  }

  function clearCaptionRequestState() {
    if (captionRequestTimeoutRef.current) {
      clearTimeout(captionRequestTimeoutRef.current);
      captionRequestTimeoutRef.current = null;
    }
    setOutgoingCaptionRequest(null);
    setIncomingCaptionRequest(null);
  }

  // Local user wants to ASK the co-parent to enable their own captions.
  // Non-blocking: just send the request and log an event. If no response
  // arrives in 30s, auto-mark as declined in the activity feed.
  function askCoparentToTranscribe() {
    if (!activeRoom) return;
    if (outgoingCaptionRequest) {
      toast.info('Already waiting for a response from your co-parent.');
      return;
    }
    if (participants.length === 0) {
      toast.error('Your co-parent is not connected yet.');
      return;
    }
    var reqId = _makeReqId();
    setOutgoingCaptionRequest({ request_id: reqId, sent_at: Date.now() });
    pushLocalEvent('caption_request_sent', selfName);
    sendDataTrackMessage({
      kind: 'caption_request',
      request_id: reqId,
      requester_name: selfName,
    });
    // 30-second timeout — treat silence as a decline in the activity feed.
    if (captionRequestTimeoutRef.current) clearTimeout(captionRequestTimeoutRef.current);
    captionRequestTimeoutRef.current = setTimeout(function () {
      setOutgoingCaptionRequest(null);
      captionRequestTimeoutRef.current = null;
      pushLocalEvent('caption_request_timeout', selfName);
    }, 30000);
  }

  // Local user responds to an inbound caption request. `granted=true` turns
  // their own captions ON; `false` just declines. Either way we notify the
  // requester so they see the verdict in their activity feed.
  function respondToCaptionRequest(granted) {
    var req = incomingCaptionRequest;
    if (!req) return;
    setIncomingCaptionRequest(null);
    if (granted && !speechSupported) {
      // Be honest with the peer — we can't transcribe ourselves at all.
      sendDataTrackMessage({
        kind: 'caption_request_response',
        request_id: req.request_id,
        granted: false,
        reason: 'speech_unsupported',
      });
      toast.error('Live captions are not supported in this browser.');
      pushLocalEvent('caption_request_declined', selfName, { meta: { reason: 'speech_unsupported' } });
      return;
    }
    sendDataTrackMessage({
      kind: 'caption_request_response',
      request_id: req.request_id,
      granted: !!granted,
    });
    if (granted) {
      setCaptionsOn(true);
      if (twRoom) startSpeechRecognition();
      pushLocalEvent('caption_request_accepted', selfName);
    } else {
      pushLocalEvent('caption_request_declined', selfName);
    }
  }

  // Auto-enable banner: the caller asked for dual transcription, so we turned
  // the callee's captions ON automatically. Banner is informational + offers a
  // one-click opt-out (turns the callee's own captions back OFF).
  function dismissDualPrompt() {
    setDualPromptOpen(false);
    pushLocalEvent('dual_transcription_acknowledged', selfName);
  }
  function optOutOfDualTranscription() {
    setDualPromptOpen(false);
    if (captionsOn) {
      stopSpeechRecognition();
      setCaptionsOn(false);
      try {
        sendDataTrackMessage({ kind: 'caption', speaker: selfName, text: '', final: false });
      } catch (e) { /* ignore */ }
      lastBroadcastInterimRef.current = '';
    }
    pushLocalEvent('dual_transcription_opted_out', selfName);
  }

  // Inbound DataTrack message handler — captions + caption-request flow.
  function handleRemoteConsentMessage(raw, fromName, roomSid) {
    var msg;
    try { msg = JSON.parse(typeof raw === 'string' ? raw : String(raw)); }
    catch (e) { return; }
    if (!msg || !msg.kind) return;

    if (msg.kind === 'caption') {
      // The peer's local Web Speech API produced a caption — render it on
      // OUR side so the user sees both speakers. Each side broadcasts its
      // own captions because the browser can only transcribe local mic.
      var speakerName = msg.speaker || fromName || 'Co-parent';
      if (msg.final) {
        if (!msg.text) return;
        var elapsed = Math.floor((Date.now() - (callStartRef.current || Date.now())) / 1000);
        setCaptions(function (prev) {
          return prev.concat([{
            speaker: speakerName,
            text: msg.text,
            start_seconds: typeof msg.start_seconds === 'number' ? msg.start_seconds : elapsed,
            end_seconds: typeof msg.start_seconds === 'number' ? msg.start_seconds : elapsed,
            final: true,
          }]);
        });
        // Clear remote-interim now that a final landed.
        setRemoteInterimCaption('');
        setRemoteInterimSpeaker('');
      } else {
        // Interim — show under the live activity feed below the local interim.
        setRemoteInterimCaption(msg.text || '');
        setRemoteInterimSpeaker(speakerName);
      }
      return;
    }

    if (msg.kind === 'caption_request') {
      // The co-parent is asking US to turn on our own captions. Non-blocking
      // banner — call continues normally.
      setIncomingCaptionRequest({
        request_id: msg.request_id,
        requester_name: msg.requester_name || fromName || 'Co-parent',
      });
      pushLocalEvent('caption_request_received', msg.requester_name || fromName, {
        roomSid: roomSid,
      });
      return;
    }

    if (msg.kind === 'caption_request_response') {
      // We're the requester. Log the verdict in the activity feed and clear.
      if (captionRequestTimeoutRef.current) {
        clearTimeout(captionRequestTimeoutRef.current);
        captionRequestTimeoutRef.current = null;
      }
      setOutgoingCaptionRequest(null);
      pushLocalEvent(msg.granted ? 'caption_request_accepted' : 'caption_request_declined', fromName, {
        roomSid: roomSid,
      });
      return;
    }
  }

  // Hook into Twilio RemoteParticipant track events so we surface mic/cam
  // toggles from the OTHER party in the same activity feed.
  function attachRemoteTrackEvents(participant, roomSid) {
    var who = resolveParticipantName(participant.identity);
    var trackSeen = function (publication) {
      var t = publication && publication.track;
      if (!t) return;
      if (t.kind === 'audio') {
        // Twilio fires .on('disabled') / .on('enabled') for remote tracks.
        t.on('disabled', function () { pushLocalEvent('mic_muted', who, { roomSid: roomSid }); });
        t.on('enabled', function () { pushLocalEvent('mic_unmuted', who, { roomSid: roomSid }); });
        // Capture the initial state when we first see the track.
        if (t.isEnabled === false) pushLocalEvent('mic_muted', who, { roomSid: roomSid });
      } else if (t.kind === 'video') {
        t.on('disabled', function () { pushLocalEvent('camera_off', who, { roomSid: roomSid }); });
        t.on('enabled', function () { pushLocalEvent('camera_on', who, { roomSid: roomSid }); });
        if (t.isEnabled === false) pushLocalEvent('camera_off', who, { roomSid: roomSid });
      } else if (t.kind === 'data') {
        // Consent-flow signaling channel — listen for messages from the OTHER peer.
        if (remoteDataTracksRef.current.indexOf(t) === -1) {
          remoteDataTracksRef.current.push(t);
        }
        t.on('message', function (raw) {
          handleRemoteConsentMessage(raw, who, roomSid);
        });
      }
    };
    participant.tracks.forEach(trackSeen);
    participant.on('trackSubscribed', function (track) { trackSeen({ track: track }); });
  }

  function joinExisting(room) {
    enterPreroom('join', room);
  }

  // If the user accepted an incoming call from the centered overlay, the room
  // was persisted to sessionStorage as the handoff vehicle (no fragile event
  // timing). Pick it up on mount AND on a re-fired `navigate-comms-video`
  // event — the latter covers the case where VideoCalls is already mounted
  // (e.g. user was sitting on Comms → Video when the call arrived) so accepting
  // wouldn't otherwise trigger a remount.
  useEffect(function () {
    function pickUpPendingJoin() {
      // Don't slam the user out of an active in-call to join another one —
      // OR out of a pre-room that's already targeting a DIFFERENT join room.
      // But if they're sitting in the "Start Call" pre-room (mode === 'new')
      // and the other party initiates a call, swap the pre-room into 'join'
      // mode so the button flips from "Start Call" → "Join Call" automatically.
      if (phase === 'in-call') return;
      var raw = null;
      try { raw = sessionStorage.getItem('parentum_pending_video_join'); } catch (e) { /* ignore */ }
      if (!raw) return;
      try { sessionStorage.removeItem('parentum_pending_video_join'); } catch (e) { /* ignore */ }
      try {
        var room = JSON.parse(raw);
        if (!room || !room.room_sid) return;
        if (phase === 'preroom') {
          // Already in pre-room. Two sub-cases:
          if (preroomMode === 'join' && preroomRoomToJoin && preroomRoomToJoin.room_sid === room.room_sid) {
            // Same room we're already heading into — nothing to do.
            return;
          }
          // Swap the pre-room target to the incoming call (covers both
          // mode==='new' wanting to start a call AND mode==='join' for a stale
          // older invite). The user's camera/preview state stays intact.
          setPreroomMode('join');
          setPreroomRoomToJoin(room);
          try {
            toast.success('Joining ' + (room.initiator_name || 'co-parent') + "'s call — click Join Call to enter.");
          } catch (e) { /* toast may not be ready */ }
          return;
        }
        // Lobby — full enter.
        enterPreroom('join', room);
      } catch (e) { /* ignore */ }
    }
    pickUpPendingJoin();
    window.addEventListener('navigate-comms-video', pickUpPendingJoin);
    return function () {
      window.removeEventListener('navigate-comms-video', pickUpPendingJoin);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, preroomMode]);

  function declineIncoming(room) {
    if (!window.confirm('Decline this call invitation from ' + (room.initiator_name || 'your co-parent') + '?')) return;
    axios.post(API + '/video/rooms/' + room.room_sid + '/decline', {}, { withCredentials: true })
      .then(function () {
        toast.success('Call declined');
        loadRooms();
      })
      .catch(function (e) {
        var msg = (e && e.response && e.response.data && e.response.data.detail) || 'Could not decline call.';
        toast.error(msg);
      });
  }

  function endCall() {
    stopSpeechRecognition();
    var roomSid = activeRoom && activeRoom.room_sid;
    var finalCaptions = captions.slice();
    // Surface the end in the live activity feed AND log to backend.
    if (roomSid) pushLocalEvent('call_ended', selfName, { roomSid: roomSid });
    if (twRoom) {
      try { twRoom.disconnect(); } catch (e) { /* ignore */ }
    }
    if (roomSid) {
      flushCaptionsToServer(roomSid, finalCaptions)
        .finally(function () {
          axios.post(API + '/video/rooms/' + roomSid + '/complete', {}, { withCredentials: true })
            .then(function (r) {
              var d = r && r.data;
              if (d && d.status === 'failed' && d.failure_reason) {
                toast.error('Call ended — ' + d.failure_reason, { duration: 8000 });
              } else {
                toast.success('Call ended. Transcript ready — higher-accuracy version processing.');
              }
            })
            .catch(function () { /* swallow — server will resolve via webhook */ })
            .finally(function () { loadRooms(); });
        });
    }
    cleanupAfterCall();
  }

  function cleanupAfterCall() {
    callStartRef.current = null;
    stopSpeechRecognition();
    detachBackgroundProcessor();
    if (twRoom) {
      try {
        twRoom.localParticipant.tracks.forEach(function (pub) {
          if (pub.track && pub.track.stop) pub.track.stop();
        });
      } catch (e) { /* ignore */ }
    }
    if (captionRequestTimeoutRef.current) { clearTimeout(captionRequestTimeoutRef.current); captionRequestTimeoutRef.current = null; }
    dataTrackRef.current = null;
    remoteDataTracksRef.current = [];
    setOutgoingCaptionRequest(null);
    setIncomingCaptionRequest(null);
    setDualPromptOpen(false);
    setTwRoom(null);
    setActiveRoom(null);
    setParticipants([]);
    setMicEnabled(true);
    setCamEnabled(true);
    setCaptionsOn(true); // ⭐ reset so the NEXT call starts with transcription ON by default
    setCaptions([]);
    setInterimCaption('');
    setRemoteInterimCaption('');
    setRemoteInterimSpeaker('');
    lastBroadcastInterimRef.current = '';
    setPhase('lobby');
    setPreroomMode(null);
    setPreroomRoomToJoin(null);
    setRequestDualTranscription(false);
    setBgChoice('none');
  }

  function toggleCaptions() {
    if (!speechSupported) {
      toast.error('Live captions are not supported in this browser. Try Chrome or Edge.');
      return;
    }
    if (captionsOn) {
      // Turn OFF my own captions — no consent needed. Tell the peer to clear
      // any in-flight interim we may have left on their screen.
      stopSpeechRecognition();
      setCaptionsOn(false);
      pushLocalEvent('captions_disabled', selfName);
      try {
        sendDataTrackMessage({ kind: 'caption', speaker: selfName, text: '', final: false });
      } catch (e) { /* ignore */ }
      lastBroadcastInterimRef.current = '';
    } else {
      // Turn ON my own captions — no consent needed. Web Speech API only
      // listens to MY mic, so this never captures the co-parent's voice.
      setCaptionsOn(true);
      if (twRoom && participants.length > 0) startSpeechRecognition();
      pushLocalEvent('captions_enabled', selfName);
    }
  }

  function toggleMic() {
    if (!twRoom) return;
    var nextOn = !micEnabled;
    twRoom.localParticipant.audioTracks.forEach(function (pub) {
      if (micEnabled) pub.track.disable(); else pub.track.enable();
    });
    setMicEnabled(nextOn);
    pushLocalEvent(nextOn ? 'mic_unmuted' : 'mic_muted', selfName);
  }

  function toggleCam() {
    if (!twRoom) return;
    var nextOn = !camEnabled;
    twRoom.localParticipant.videoTracks.forEach(function (pub) {
      if (camEnabled) pub.track.disable(); else pub.track.enable();
    });
    setCamEnabled(nextOn);
    pushLocalEvent(nextOn ? 'camera_on' : 'camera_off', selfName);
  }

  function deleteRoom(room) {
    if (!window.confirm('Delete this recording and transcript? This cannot be undone.')) return;
    axios.delete(API + '/video/rooms/' + room.room_sid, { withCredentials: true })
      .then(function () { toast.success('Deleted'); loadRooms(); })
      .catch(function () { toast.error('Delete failed'); });
  }

  function downloadRoomFile(room, kind) {
    var url = API + '/video/rooms/' + room.room_sid + '/download/' + kind;
    window.open(url, '_blank');
  }

  function addTranscriptToJournal(room) {
    // Fetch decrypted transcript text + call metadata, stash it in
    // sessionStorage, then route the user to the Journal tab where the
    // create form auto-opens with the prefill applied (see Journal.jsx).
    axios.get(API + '/video/rooms/' + room.room_sid + '/transcript-text', { withCredentials: true })
      .then(function (resp) {
        var d = resp && resp.data;
        if (!d || !d.transcript_text) {
          toast.error('Transcript is not yet available for this call.');
          return;
        }
        try {
          window.sessionStorage.setItem('parentum_journal_prefill', JSON.stringify({
            source: 'video_call',
            room_sid: d.room_sid,
            transcript_text: d.transcript_text,
            started_at: d.started_at,
            duration_seconds: d.duration_seconds,
            partner_name: d.partner_name,
          }));
        } catch (e) { /* sessionStorage disabled — fall through, the toast will explain */ }
        window.dispatchEvent(new CustomEvent('navigate-journal-from-call'));
      })
      .catch(function (err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail) || 'Could not load transcript yet. Please try again in a moment.';
        toast.error(msg);
      });
  }

  // ---------------- Render ----------------
  if (loading) {
    return <div className="card-surface p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#5A4E9E]" /></div>;
  }

  if (!caps || !caps.can_call) {
    return (
      <div className="p-8 rounded-2xl bg-gradient-to-br from-[#2D2560] to-[#5A4E9E] text-white shadow-lg text-center" data-testid="video-locked">
        <Lock className="w-10 h-10 text-white mx-auto mb-3" />
        <p className="text-lg font-semibold mb-1">Video Calling Unavailable</p>
        <p className="text-sm text-white/85 mb-4">Live video calls with recording and transcription are available on Standard and Pro plans.</p>
        <button onClick={function () { window.dispatchEvent(new CustomEvent('navigate-billing')); }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-[#2D2560] text-sm font-semibold hover:bg-white/90 transition-colors"
          data-testid="video-upgrade-btn">
          Upgrade
        </button>
      </div>
    );
  }

  // Pre-room view: camera/mic preview before joining the call.
  if (phase === 'preroom') {
    var preroomTitle = preroomMode === 'join'
      ? 'Joining call with ' + (preroomRoomToJoin && preroomRoomToJoin.partner_name ? preroomRoomToJoin.partner_name : preroomRoomToJoin && preroomRoomToJoin.initiator_name ? preroomRoomToJoin.initiator_name : 'co-parent')
      : 'Ready to call your co-parent?';
    return (
      <div className="space-y-0" data-testid="video-preroom">
        <div className="card-surface p-5 space-y-5">
          {/* Waiting-room banner */}
          <div
            className="rounded-xl border border-[#F5C26B] bg-gradient-to-r from-[#FFF7E6] to-[#FFEFD0] p-4 flex items-start gap-3"
            data-testid="video-preroom-waiting-banner"
            role="status"
            aria-live="polite"
          >
            <div className="flex-shrink-0 mt-0.5">
              <span className="inline-flex w-8 h-8 rounded-full bg-[#F5C26B]/30 items-center justify-center">
                <Video className="w-4 h-4 text-[#8A5A00]" />
              </span>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#5C3A00]">You are in the waiting room</p>
              <p className="text-xs text-[#7A4E00] mt-0.5">
                Your monthly call minutes are <span className="font-semibold">not</span> being used yet. Time only starts counting when you click {preroomMode === 'join' ? '“Join Call”' : '“Start Call”'} below.
              </p>
            </div>
          </div>

          {/* Title */}
          <div>
            <h3 className="text-lg font-semibold text-[#1E1A3E] mb-1">{preroomTitle}</h3>
            <p className="text-sm text-[#6B6690]">Test your camera and microphone, then click {preroomMode === 'join' ? '“Join Call”' : '“Start Call”'} when you're ready.</p>
          </div>

          {/* Camera preview */}
          <div className="relative rounded-xl overflow-hidden bg-black aspect-video border border-[#DDE1EE] mx-auto max-w-2xl">
            <video ref={previewVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="video-preroom-preview" />
            {!previewCamEnabled && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white text-sm">
                <VideoOff className="w-8 h-8 mr-2" /> Camera off
              </div>
            )}
            {previewError && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white text-sm p-4 text-center">
                {previewError}
              </div>
            )}
            <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/60 text-white text-xs font-medium">You (preview)</div>
            {!previewMicEnabled && (
              <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-[#EF4444] text-white text-xs font-medium flex items-center gap-1">
                <MicOff className="w-3 h-3" /> Muted
              </div>
            )}
          </div>

          {/* Mic / Cam toggle row */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button onClick={togglePreviewMic} className={'p-3 rounded-full border transition-colors ' + (previewMicEnabled ? 'bg-white border-[#DDE1EE] text-[#1E1A3E]' : 'bg-[#EF4444] border-[#EF4444] text-white')} data-testid="video-preroom-mic-btn" aria-label="Toggle microphone">
              {previewMicEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
            </button>
            <button onClick={togglePreviewCam} className={'p-3 rounded-full border transition-colors ' + (previewCamEnabled ? 'bg-white border-[#DDE1EE] text-[#1E1A3E]' : 'bg-[#EF4444] border-[#EF4444] text-white')} data-testid="video-preroom-cam-btn" aria-label="Toggle camera">
              {previewCamEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
            </button>
          </div>

          {preroomMode === 'new' && (
            <p className="text-[11px] text-center text-[#6B6690] px-4" data-testid="video-preroom-transcript-notice">
              A live transcript of the conversation will be saved automatically. <span className="font-semibold">No audio or video is recorded.</span>
            </p>
          )}
          {preroomMode === 'join' && (
            <p className="text-[11px] text-center text-[#6B6690] px-4">
              A live transcript will be saved automatically. No audio or video is recorded.
            </p>
          )}

          {/* Caller-only: request dual transcription. The callee gets a
              non-blocking banner on join asking them to transcribe themselves
              too. They can still accept the call without enabling captions. */}
          {preroomMode === 'new' && (
            <label className="flex items-start gap-3 max-w-xl mx-auto p-3 rounded-lg border border-[#DDE1EE] bg-white cursor-pointer hover:bg-[#F6F7FC]" data-testid="video-preroom-dual-transcription">
              <input
                type="checkbox"
                checked={requestDualTranscription}
                onChange={function (e) { setRequestDualTranscription(e.target.checked); }}
                className="mt-0.5 h-4 w-4 rounded border-[#DDE1EE] text-[#5A4E9E] focus:ring-[#5A4E9E]"
                data-testid="video-preroom-dual-transcription-checkbox"
              />
              <span className="flex-1 text-xs text-[#3A3566]">
                <span className="font-semibold text-[#1E1A3E]">Ask co-parent to transcribe themselves too.</span>{' '}
                Your captions only transcribe your own voice. With this checked, your co-parent will see a non-blocking prompt to enable transcription of their voice when they join.
              </span>
            </label>
          )}

          {/* Virtual background selector */}
          <BackgroundSelector
            bgChoice={bgChoice}
            applying={bgApplying}
            savedBgs={savedBgs}
            savedLimitReached={savedBgs.length >= 3}
            maxSaved={3}
            onPick={applyBackground}
            onPickCustomFile={onCustomBgSelected}
            onDeleteSaved={onDeleteSavedBg}
            fileInputRef={customFileInputRef}
            disabled={!!previewError || !previewTracks}
          />

          {/* Cancel / Start-Join action row */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2 border-t border-[#EEEFF8]">
            <button onClick={cancelPreroom} className="w-full sm:w-auto px-5 py-2.5 rounded-lg border border-[#DDE1EE] text-sm font-medium text-[#3A3566] hover:bg-[#F6F7FC]" data-testid="video-preroom-cancel-btn">
              Cancel
            </button>
            <button onClick={joinFromPreroom} disabled={starting || !!previewError}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40"
              data-testid="video-preroom-join-btn">
              <Phone className="w-4 h-4" />
              {starting ? 'Connecting…' : (preroomMode === 'join' ? 'Join Call' : 'Start Call')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active call view
  if (phase === 'in-call' && (!twRoom || !activeRoom)) {
    return (
      <div className="card-surface p-8 text-center" data-testid="video-connecting">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#5A4E9E] mb-2" />
        <p className="text-sm text-[#3A3566]">Connecting to your co-parent…</p>
      </div>
    );
  }

  if (twRoom && activeRoom) {
    var localP = twRoom.localParticipant;
    return (
      <div className="space-y-4" data-testid="video-active-call">
        {/* Live transcript banner — no audio/video is recorded; the in-call
            captions box IS the durable record. */}
        <div className="flex items-center justify-between gap-3 p-3 rounded-xl border bg-[#5A4E9E]/10 border-[#5A4E9E]/40">
          <div className="flex items-center gap-2 text-sm">
            <Captions className="w-4 h-4 text-[#5A4E9E]" />
            <span className="font-semibold text-[#2D2560]">Live transcript</span>
            <span className="text-[#3A3566] hidden sm:inline">— what appears in the activity feed is what gets saved. No audio or video is recorded.</span>
          </div>
          <div className="text-sm font-mono text-[#1E1A3E] flex items-center gap-2" data-testid="video-time-left">
            <span
              className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#2D2560]/10 text-[#2D2560] text-[11px] font-semibold"
              title="Audio and video are encrypted in transit (SRTP/DTLS) and NEVER recorded. Transcripts are encrypted at rest in our database."
              data-testid="video-encrypted-badge"
            >
              <ShieldCheck className="w-3 h-3" />
              Encrypted
            </span>
            <span>{Math.floor(secondsLeft / 60)}:{(secondsLeft % 60).toString().padStart(2, '0')}</span>
          </div>
        </div>

        {/* Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {localP && <Participant participant={localP} isLocal displayName={selfName} />}
          {participants.map(function (p) {
            return <Participant key={p.sid} participant={p} isLocal={false} displayName={resolveParticipantName(p.identity)} />;
          })}
        </div>

        {/* Live activity feed — events + captions interleaved by call timestamp */}
        <div className="card-surface p-4 relative" data-testid="video-activity-feed">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#5A4E9E] flex items-center gap-1.5">
              <Captions className="w-3.5 h-3.5" /> Live activity
            </p>
            <span className="text-[10px] text-[#6B6690]">
              {participants.length === 0
                ? 'Transcription starts when the other party joins'
                : (captionsOn && speechSupported ? 'Captions live · ' : '') + 'Events visible to both parties'}
            </span>
          </div>
          <div
            ref={activityFeedRef}
            onScroll={handleActivityScroll}
            className="space-y-1 min-h-[3.5rem] max-h-48 overflow-y-auto pr-1 scroll-smooth"
            data-testid="video-activity-scroll"
          >
            {(function () {
              var items = [];
              callEvents.forEach(function (e) { items.push(e); });
              captions.forEach(function (c, i) {
                items.push({
                  id: 'c' + i,
                  kind: 'caption',
                  at_seconds: c.start_seconds,
                  speaker: c.speaker,
                  text: c.text,
                  ts: c.ts || 0,
                });
              });
              items.sort(function (a, b) {
                if (a.at_seconds !== b.at_seconds) return a.at_seconds - b.at_seconds;
                return (a.ts || 0) - (b.ts || 0);
              });
              return items.slice(-20);
            })().map(function (it) {
              var stamp = ('00' + Math.floor(it.at_seconds / 60)).slice(-2) + ':' + ('00' + (it.at_seconds % 60)).slice(-2);
              if (it.kind === 'event') {
                var EVENT_LABELS = {
                  waiting_room_entered: 'entered the waiting room',
                  call_initiated: 'placed the call',
                  call_started: 'joined the call',
                  participant_joined: 'joined the call',
                  participant_left: 'left the call',
                  call_ended: 'ended the call',
                  recording_enabled: 'turned recording ON',
                  recording_disabled: 'turned recording OFF',
                  captions_enabled: 'turned transcription ON',
                  captions_disabled: 'turned transcription OFF',
                  caption_request_sent: 'asked the co-parent to transcribe themselves',
                  caption_request_received: 'asked you to transcribe yourself',
                  caption_request_accepted: 'accepted the transcription request',
                  caption_request_declined: 'declined the transcription request',
                  caption_request_timeout: "didn't get a response to their transcription request",
                  dual_transcription_auto_enabled: 'auto-enabled their captions (dual transcription)',
                  dual_transcription_acknowledged: 'acknowledged dual transcription',
                  dual_transcription_opted_out: 'turned off their own captions',
                  mic_muted: 'muted their microphone',
                  mic_unmuted: 'unmuted their microphone',
                  camera_off: 'turned camera OFF',
                  camera_on: 'turned camera ON',
                };
                var EVENT_DOT = {
                  waiting_room_entered: 'bg-amber-400',
                  call_initiated: 'bg-violet-500',
                  call_started: 'bg-emerald-500',
                  participant_joined: 'bg-emerald-500',
                  participant_left: 'bg-rose-500',
                  call_ended: 'bg-rose-600',
                  recording_enabled: 'bg-rose-500',
                  recording_disabled: 'bg-zinc-400',
                  captions_enabled: 'bg-violet-500',
                  captions_disabled: 'bg-zinc-400',
                  caption_request_sent: 'bg-violet-500',
                  caption_request_received: 'bg-violet-500',
                  caption_request_accepted: 'bg-emerald-500',
                  caption_request_declined: 'bg-zinc-400',
                  caption_request_timeout: 'bg-zinc-400',
                  dual_transcription_auto_enabled: 'bg-violet-500',
                  dual_transcription_acknowledged: 'bg-emerald-500',
                  dual_transcription_opted_out: 'bg-zinc-400',
                  mic_muted: 'bg-rose-500',
                  mic_unmuted: 'bg-emerald-500',
                  camera_off: 'bg-rose-500',
                  camera_on: 'bg-emerald-500',
                };
                var label = EVENT_LABELS[it.type] || it.type.replace(/_/g, ' ');
                var dot = EVENT_DOT[it.type] || 'bg-[#6B6690]';
                // Defensive scrub: never show a raw user_id in the live feed.
                var whoLabel = it.who || '';
                if (whoLabel && (/^[a-f0-9]{24}$/i.test(whoLabel) || /^[0-9a-f-]{36}$/i.test(whoLabel))) {
                  whoLabel = '';
                }
                return (
                  <p key={'e-' + it.id} className="text-xs text-[#3A3566] flex items-center gap-2" data-testid={'video-event-' + it.type}>
                    <span className="font-mono text-[10px] text-[#6B6690]">{stamp}</span>
                    <span className={'inline-block w-1.5 h-1.5 rounded-full ' + dot}></span>
                    <span><span className="font-semibold text-[#1E1A3E]">{whoLabel || 'Someone'}</span> {label}</span>
                  </p>
                );
              }
              return (
                <p key={'cap-' + it.id} className="text-sm text-[#1E1A3E]">
                  <span className="font-mono text-[11px] text-[#6B6690] mr-2">{stamp}</span>
                  {it.speaker ? <span className="font-semibold mr-1">{it.speaker}:</span> : null}
                  {it.text}
                </p>
              );
            })}
            {captionsOn && speechSupported && interimCaption && (
              <p className="text-sm italic text-[#5A4E9E]" data-testid="video-caption-interim">
                <span className="font-semibold mr-1">{selfName}:</span>{interimCaption}
              </p>
            )}
            {remoteInterimCaption && (
              <p className="text-sm italic text-[#5A4E9E]" data-testid="video-caption-remote-interim">
                <span className="font-semibold mr-1">{remoteInterimSpeaker || 'Co-parent'}:</span>{remoteInterimCaption}
              </p>
            )}
            {callEvents.length === 0 && captions.length === 0 && !interimCaption && !remoteInterimCaption && (
              <p className="text-xs text-[#6B6690] italic">Waiting for activity…</p>
            )}
          </div>
          {showJumpToLatest && (
            <button
              type="button"
              onClick={jumpToLatest}
              className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2D2560] text-white text-xs font-semibold shadow-md hover:bg-[#5A4E9E] transition-colors"
              data-testid="video-activity-jump-latest"
              aria-label={unreadCount > 0 ? unreadCount + ' new — jump to latest' : 'Jump to latest activity'}
            >
              ↓ {unreadCount > 0 ? (unreadCount > 99 ? '99+ new' : unreadCount + ' new') : 'Jump to latest'}
            </button>
          )}
        </div>
        {!speechSupported && (
          <div className="card-surface p-3 text-xs text-[#6B6690]">
            Live captions need Chrome, Edge, or Safari. Call events still appear above and the call will still be recorded and transcribed after it ends.
          </div>
        )}

        {/* Non-blocking caption-request banners (no call freeze). */}
        {dualPromptOpen && (
          <div className="card-surface p-4 border border-[#5A4E9E]/40 bg-[#F1EEFF]" data-testid="video-dual-prompt-banner">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <Captions className="w-4 h-4 text-[#2D2560] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1E1A3E]">Dual transcription is active</p>
                  <p className="text-xs text-[#3A3566] mt-0.5">Your co-parent asked to transcribe both voices. Your captions are ON so your microphone is being transcribed too.</p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={optOutOfDualTranscription} className="px-3 py-1.5 rounded-lg border border-[#DDE1EE] text-xs font-medium text-[#3A3566] hover:bg-white" data-testid="video-dual-prompt-optout">
                  Turn off my captions
                </button>
                <button onClick={dismissDualPrompt} className="px-3 py-1.5 rounded-lg bg-[#2D2560] text-white text-xs font-semibold hover:bg-[#5A4E9E]" data-testid="video-dual-prompt-acknowledge">
                  Got it
                </button>
              </div>
            </div>
          </div>
        )}
        {incomingCaptionRequest && (
          <div className="card-surface p-4 border border-[#10B981]/40 bg-[#ECFDF5]" data-testid="video-caption-request-banner">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <Captions className="w-4 h-4 text-[#0F6A4A] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#0F3F2E]">
                    {incomingCaptionRequest.requester_name} asked you to transcribe yourself
                  </p>
                  <p className="text-xs text-[#0F6A4A] mt-0.5">Your browser will transcribe only your microphone — the call continues either way.</p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={function () { respondToCaptionRequest(false); }} className="px-3 py-1.5 rounded-lg border border-[#DDE1EE] text-xs font-medium text-[#3A3566] hover:bg-white" data-testid="video-caption-request-decline">
                  Decline
                </button>
                <button onClick={function () { respondToCaptionRequest(true); }} className="px-3 py-1.5 rounded-lg bg-[#10B981] text-white text-xs font-semibold hover:bg-[#059669]" data-testid="video-caption-request-accept">
                  Yes, transcribe me
                </button>
              </div>
            </div>
          </div>
        )}
        {outgoingCaptionRequest && (
          <div className="card-surface p-3 border border-[#5A4E9E]/30 bg-[#F6F7FC]" data-testid="video-caption-request-pending">
            <p className="text-xs text-[#3A3566] flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-[#5A4E9E]" />
              Waiting for your co-parent to decide on your transcription request…
            </p>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-center gap-3 p-4 card-surface">
          <button onClick={toggleMic} className={'p-3 rounded-full border transition-colors ' + (micEnabled ? 'bg-white border-[#DDE1EE] text-[#1E1A3E]' : 'bg-[#EF4444] border-[#EF4444] text-white')} data-testid="video-mic-btn">
            {micEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </button>
          <button onClick={toggleCam} className={'p-3 rounded-full border transition-colors ' + (camEnabled ? 'bg-white border-[#DDE1EE] text-[#1E1A3E]' : 'bg-[#EF4444] border-[#EF4444] text-white')} data-testid="video-cam-btn">
            {camEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </button>
          <button onClick={toggleCaptions}
            className={'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-xs font-semibold transition-colors ' + (captionsOn ? 'bg-[#5A4E9E] border-[#5A4E9E] text-white' : 'bg-white border-[#DDE1EE] text-[#1E1A3E] hover:bg-[#F6F7FC]')}
            data-testid="video-captions-btn"
            aria-pressed={captionsOn}
            title={captionsOn ? 'Stop transcribing me' : 'Transcribe me (no consent needed for your own voice)'}>
            {captionsOn ? <Captions className="w-4 h-4" /> : <CaptionsOff className="w-4 h-4" />}
            {captionsOn ? 'Transcribing me' : 'Transcribe me'}
          </button>
          <button onClick={askCoparentToTranscribe}
            disabled={!!outgoingCaptionRequest || participants.length === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#5A4E9E] text-white font-semibold hover:bg-[#2D2560] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="video-ask-coparent-transcribe-btn"
            title="Ask co-parent to also transcribe their own voice">
            <Captions className="w-5 h-5" />
            Ask co-parent to transcribe
          </button>
          <button onClick={endCall} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#EF4444] text-white font-semibold hover:opacity-90" data-testid="video-end-btn">
            <PhoneOff className="w-5 h-5" /> End call
          </button>
        </div>

        {/* In-call virtual background selector */}
        <BackgroundSelector
          bgChoice={bgChoice}
          applying={bgApplying}
          savedBgs={savedBgs}
          savedLimitReached={savedBgs.length >= 3}
          maxSaved={3}
          onPick={applyBackground}
          onPickCustomFile={onCustomBgSelected}
          onDeleteSaved={onDeleteSavedBg}
          fileInputRef={customFileInputRef}
          disabled={!camEnabled}
          compact
        />
      </div>
    );
  }

  // Lobby
  var pendingForMe = rooms.filter(function (r) {
    return r.status === 'pending' && r.invited_user_id;
  });

  return (
    <div className="space-y-6" data-testid="video-calls-page">
      {/* Quota header */}
      <div className="card-surface p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-[#1E1A3E] mb-1 flex items-center gap-2">
            <Video className="w-5 h-5 text-[#5A4E9E]" /> Video Calling
          </h3>
          <p className="text-xs text-[#3A3566]" data-testid="video-quota-line">
            {caps.unlimited_minutes
              ? 'Unlimited video minutes, unlimited calls.'
              : (caps.remaining_minutes_this_month + ' of ' + caps.monthly_minute_limit + ' min remaining this month · unlimited calls')}
          </p>
          <p className="text-xs text-[#6B6690] mt-1">
            Retention: {caps.tier === 'pro' ? 'kept until you delete' : caps.retention_days + ' days'}.
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2">
          <p className="text-[11px] text-[#6B6690] flex items-center gap-1.5">
            <Captions className="w-3 h-3" /> Live transcript auto-saved · no audio/video recorded
          </p>
          <button onClick={startCall} disabled={starting || (!caps.unlimited_minutes && caps.remaining_minutes_this_month <= 0)}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40"
            data-testid="video-start-btn">
            <Phone className="w-4 h-4" /> {starting ? 'Connecting…' : 'Start Video Call'}
          </button>
        </div>
      </div>

      {pendingForMe.length > 0 && (
        <div className="card-surface p-5 border border-[#10B981]/30 bg-[#10B981]/5" data-testid="video-pending">
          <p className="text-sm font-semibold text-[#0F6A4A] mb-2">Incoming call invitation</p>
          <div className="space-y-2">
            {pendingForMe.map(function (r) {
              return (
                <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#DDE1EE] bg-white">
                  <div>
                    <p className="text-sm text-[#1E1A3E] font-medium">From {r.initiator_name || 'Co-parent'}</p>
                    <p className="text-xs text-[#6B6690]">Started {fmtDate(r.created_at)}</p>
                    {r.dual_transcription_requested && (
                      <p className="text-[11px] text-[#2D2560] font-semibold mt-1 inline-flex items-center gap-1" data-testid={'video-dual-trans-badge-' + r.id}>
                        <Captions className="w-3 h-3" /> Dual transcription requested
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={function () { joinExisting(r); }}
                      className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white text-xs font-semibold"
                      data-testid={'video-join-' + r.id}>
                      Join
                    </button>
                    <button onClick={function () { declineIncoming(r); }}
                      className="px-3 py-1.5 rounded-lg border border-[#DDE1EE] text-[#3A3566] text-xs font-medium hover:bg-[#FEF2F2] hover:text-[#EF4444] hover:border-[#EF4444]/40"
                      data-testid={'video-decline-' + r.id}>
                      Decline
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card-surface p-5">
        <h3 className="text-base font-semibold text-[#1E1A3E] mb-3">Recent calls</h3>
        {rooms.length === 0 ? (
          <p className="text-sm text-[#6B6690] text-center py-6">No calls yet. Start one above.</p>
        ) : (
          <div className="space-y-2">
            {rooms.map(function (r) {
              return (
                <div key={r.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg border border-[#DDE1EE] bg-white" data-testid={'video-room-' + r.id}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-[#1E1A3E]" data-testid={'video-room-label-' + r.id}>
                        Call with {r.partner_name || 'co-parent'}
                      </p>
                      <StatusPill status={r.status} />
                      {!r.recording_enabled && <span className="text-[10px] uppercase tracking-wider text-[#6B6690]">No recording</span>}
                    </div>
                    <p className="text-xs text-[#6B6690]">
                      {fmtDate(r.created_at)} · {fmtDuration(r.duration_seconds)}
                      {r.retention_until && ' · purges ' + fmtDate(r.retention_until).split(',')[0]}
                    </p>
                    {r.failure_reason && (
                      <p className="text-xs text-[#B91C1C] mt-1 flex items-start gap-1.5" data-testid={'video-room-failure-' + r.id}>
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>{r.failure_reason}</span>
                      </p>
                    )}
                    {(r.recording_reference_number || r.transcript_reference_number) && (
                      <div className="flex flex-wrap gap-1 mt-1.5" title="Reference numbers for legal record certification">
                        {r.recording_reference_number && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#5A4E9E]/15 text-[#5A4E9E] font-bold">{r.recording_reference_number}</span>
                        )}
                        {r.transcript_reference_number && (
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#5A4E9E]/15 text-[#5A4E9E] font-bold">{r.transcript_reference_number}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {r.status === 'completed' && r.transcript_reference_number && (
                      <>
                        {/* Audio/video media is purged from disk + Twilio as soon as the
                            transcript is finalized — only the transcript PDF is retained
                            as the durable record. The MP4 button is therefore intentionally
                            absent. */}
                        <button onClick={function () { downloadRoomFile(r, 'transcript'); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#DDE1EE] text-xs font-medium text-[#1E1A3E] hover:bg-[#F6F7FC]"
                          data-testid={'video-dl-trans-' + r.id}>
                          <FileText className="w-3.5 h-3.5" /> Transcript PDF
                        </button>
                        <button onClick={function () { addTranscriptToJournal(r); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2D2560] text-white text-xs font-semibold hover:bg-[#5A4E9E]"
                          data-testid={'video-add-journal-' + r.id}>
                          <BookOpen className="w-3.5 h-3.5" /> Add to Journal
                        </button>
                      </>
                    )}
                    {r.status === 'processing' && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-[#5A4E9E]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing</span>
                    )}
                    <button onClick={function () { deleteRoom(r); }}
                      className="p-1.5 rounded-lg border border-[#DDE1EE] text-[#6B6690] hover:bg-[#FEF2F2] hover:text-[#EF4444]"
                      data-testid={'video-delete-' + r.id}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card-surface p-4">
        <p className="text-xs text-[#6B6690] flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B] mt-0.5 shrink-0" />
          By starting a call you confirm consent to recording and transcription per our Terms of Service. Both parties are notified by an on-screen recording indicator throughout the call.
        </p>
      </div>
    </div>
  );
}
